import { ChevronDown } from 'lucide-react';
import * as React from 'react';

import { cn } from '@/ux-common/lib/utils';

import { InputTrailingIcon } from './input';
import { useGroupField } from './input.context';
import { isEmptyTriggerValue, splitTriggerChildren } from './split-trigger-children';

export type InputTriggerFieldProps = Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'type'> & {
  /** Shown in the field while `children` is empty. */
  placeholder?: React.ReactNode;
  /**
   * The id of the element that says why the field is refused. Set, the field is
   * refused: focusable, `aria-disabled`, described by that element, and none of its
   * activation handlers run. The root that owns the refusal renders the element.
   */
  refusalId?: string;
  /** Class(es) for the value span, beside `.input-control`. */
  valueClassName?: string;
  /** Class(es) for the chevron, beside `.input-chevron`. */
  chevronClassName?: string;
};

/**
 * InputTriggerField — the Input's bordered `.input-field` drawn as a button that
 * opens something: the value (or `placeholder`), the trigger slots, and a trailing
 * chevron that turns while open. It belongs to no popup root. DropdownInputTrigger
 * and MobileMenuInputTrigger each put it under their own trigger with `asChild`,
 * which sets `aria-haspopup`, `aria-expanded` and `data-state` and adds the open
 * handlers.
 *
 * It joins the enclosing `Input` group (see `useGroupField`), so `InputLabel`'s
 * `htmlFor` and every `InputHelper`'s description reach it. Its name is the label,
 * the value and any `InputValueChange` together ("Mode Auto Previous value
 * Manual"), so a screen reader hears what is chosen without opening anything.
 * `type` is pinned to `"button"`, so it never submits a form.
 *
 * Children are split by `splitTriggerChildren`: an `InputLeadingIcon` renders
 * before the value, an `InputValueChange` and an `InputStateIcon` after it, before
 * the chevron.
 *
 * A refused field (`refusalId`) is not natively disabled, so it keeps focus and
 * hover, and a tooltip on it still fires. It drops `onClick`, `onKeyDown` and
 * `onPointerDown`. Under a trigger's `asChild` those props are the app's handlers
 * merged with the trigger's own, so dropping them both skips the app's handlers and
 * keeps the popup shut.
 */
export const InputTriggerField = React.forwardRef<HTMLButtonElement, InputTriggerFieldProps>(
  (
    {
      className,
      placeholder,
      refusalId,
      valueClassName,
      chevronClassName,
      children,
      id,
      disabled,
      'aria-describedby': ariaDescribedBy,
      'aria-labelledby': ariaLabelledBy,
      onClick,
      onKeyDown,
      onPointerDown,
      ...props
    },
    ref
  ) => {
    const refused = refusalId !== undefined;
    // A refused field gives focus by hand on a press: Safari does not focus a
    // button on click, and the press is the only thing it still answers.
    const activation = refused
      ? {
          onPointerDown: (event: React.PointerEvent<HTMLButtonElement>) =>
            event.currentTarget.focus(),
        }
      : { onClick, onKeyDown, onPointerDown };

    // Refused reads as disabled to the group too.
    const { group, controlId, setControlRef, describedBy } = useGroupField<HTMLButtonElement>({
      id,
      ref,
      disabled: Boolean(disabled) || refused,
      readOnly: false,
      ariaDescribedBy,
    });
    const valueId = `${controlId}-value`;

    const {
      leadingIcon,
      value,
      valueChange: rawValueChange,
      stateIcon,
    } = splitTriggerChildren(children);
    const valueChangeId = rawValueChange?.props.id ?? `${controlId}-value-change`;
    const valueChange = rawValueChange && React.cloneElement(rawValueChange, { id: valueChangeId });

    const empty = isEmptyTriggerValue(value);
    // `htmlFor` alone would name the button after the label and hide the value, so
    // name it after the label, the value, and any "Previous value …" together. With
    // no label in the group, the button's own text names it.
    const labelledBy =
      ariaLabelledBy ??
      (group?.labelId
        ? [group.labelId, valueId, valueChange && valueChangeId].filter(Boolean).join(' ')
        : undefined);

    return (
      <button
        ref={setControlRef}
        type="button"
        id={controlId}
        disabled={refused ? false : disabled}
        aria-labelledby={labelledBy}
        aria-describedby={[refusalId, describedBy].filter(Boolean).join(' ') || undefined}
        data-placeholder={empty ? '' : undefined}
        // A wrapper's own marker arrives through `props` and wins.
        data-component="InputTriggerField"
        className={cn('input-field input-trigger', className)}
        {...props}
        {...activation}
        {...(refused && { 'aria-disabled': true, 'data-disabled': '' })}
      >
        {leadingIcon}
        <span id={valueId} className={cn('input-control', valueClassName)}>
          {empty ? placeholder : value}
        </span>
        {valueChange}
        {stateIcon}
        <InputTrailingIcon className={cn('input-chevron', chevronClassName)}>
          <ChevronDown />
        </InputTrailingIcon>
      </button>
    );
  }
);

InputTriggerField.displayName = 'InputTriggerField';
